import { classifyEnvironmentId } from "../../environments/environmentIdClassification";

/**
 * One runtime-scoped identity for a sidebar row, whichever environment key it
 * arrived under.
 *
 * A cloud runtime reaches the sidebar under two keys. CMMD's replica and the
 * sidebar's own cloud rows name it by the legacy locator
 * (`cmmd-runtime:<runtimeId>`). A live connection made through the execution
 * intent path registers it under CMMD's durable Environment key (`env_…`,
 * FOR-32652) and replaces the locator record. Both keys name the same Sprite,
 * and the saved-environment registry is what says so: the durable record
 * carries `runtimeId`.
 *
 * Every comparison that involves a thread's environment has to go through
 * here. The live/replica merge already did; the disposition overlay, the origin
 * badge and the project icon compared raw ids, so the first send from a draft
 * flipped the whole sidebar: CMMD-settled threads popped back into Active,
 * cloud rows wore the linked-device glyph, and cloud projects lost their icon
 * (FOR-36687).
 *
 * Identity stays runtime-scoped rather than collapsing to the thread id alone,
 * because independent runtimes can legitimately reuse a provider thread id.
 */
export type SavedEnvironmentRuntimeIndex = Readonly<
  Record<string, { readonly runtimeId?: string | undefined }>
>;

/** The runtime an environment key names, or null for a desktop/local environment. */
export function resolveSidebarRuntimeId(
  environmentId: string,
  environments: SavedEnvironmentRuntimeIndex = {},
): string | null {
  const registered = environments[environmentId]?.runtimeId?.trim();
  if (registered) return registered;
  return classifyEnvironmentId(environmentId).legacyRuntimeId;
}

/** A catalog row, as far as runtime identity is concerned. */
export interface CloudRuntimeCatalogRowRef {
  readonly runtimeId: string | null;
  readonly cmmdEnvironmentId?: string | null;
}

/**
 * Everything that can say which runtime a durable Environment is hosted on.
 *
 * Two sources, because they fail in different directions. The saved record is
 * written when the connection is made and survives a Sprite replacement, so it
 * is preferred. The catalog row answers for an Environment this device has
 * never connected to, which is the whole of the sidebar's Cloud entry.
 */
export interface SidebarCloudRuntimeSources {
  readonly savedEnvironments?: SavedEnvironmentRuntimeIndex;
  readonly catalogRows?: readonly CloudRuntimeCatalogRowRef[];
}

/**
 * The runtime behind an environment key, resolved rather than parsed out of it.
 *
 * `resolveSidebarRuntimeId` answers for the legacy locator and for a durable
 * key the registry already knows. A durable key that neither names still has a
 * runtime: the catalog row carries it under `cmmdEnvironmentId` (FOR-32652),
 * which is how route attachment finds the same row (`findCloudRowForEnvironment`).
 *
 * Null means nothing on this device knows the runtime. Callers must keep such a
 * row where it already was rather than inventing a bucket for it: a cloud row
 * the catalog does not list has no Cloud entry to render under.
 */
export function resolveCloudEnvironmentRuntimeId(
  environmentId: string,
  sources: SidebarCloudRuntimeSources = {},
): string | null {
  const registered = resolveSidebarRuntimeId(environmentId, sources.savedEnvironments);
  if (registered !== null) return registered;
  const row = sources.catalogRows?.find(
    (candidate) =>
      candidate.cmmdEnvironmentId != null && candidate.cmmdEnvironmentId === environmentId,
  );
  return row?.runtimeId ?? null;
}

/**
 * Whether an environment key names a Forge Cloud runtime. The prefix forms
 * answer via `classifyEnvironmentId(...).isCloud`; the durable key answers
 * through its saved record, the same rule `isCmmdCloudEnvironment` applies to.
 */
export function isCloudSidebarEnvironment(
  environmentId: unknown,
  environments: SavedEnvironmentRuntimeIndex = {},
): boolean {
  if (classifyEnvironmentId(environmentId).isCloud) return true;
  return Boolean(environments[String(environmentId)]?.runtimeId?.trim());
}

/**
 * The key two rows share when they are the same thread (or project) on the
 * same runtime. Desktop rows keep the plain `environmentId:id` form.
 */
export function runtimeScopedSidebarKey(
  environmentId: string,
  id: string,
  environments: SavedEnvironmentRuntimeIndex = {},
): string {
  const runtimeId = resolveSidebarRuntimeId(environmentId, environments);
  return runtimeId === null ? `${environmentId}:${id}` : JSON.stringify(["runtime", runtimeId, id]);
}
